import React, { useEffect, useState } from "react";
import { ResetPasswordMutation, ForgotPasswordMutation } from "gql/mutations/auth.graphql";
import { MessageBar, MessageBarType, PrimaryButton, TextField, DefaultButton } from "office-ui-fabric-react";
import { gqlMutation } from "../common/lib";
import { LoginLayout } from "./LoginLayout";
import { PasswordRequirements, RequirementAlgs, validatePassword } from "./common";
import { Link, useLocation } from "react-router-dom";

function ResetPassword(props) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [passwordRepeat, setPasswordRepeat] = useState<string>("");
  const [redirectCountdown, setRedirectCountdown] = useState<number | undefined>(undefined);
  const [requestTokenCountdown, setRequestTokenCountdown] = useState<number>(0);
  const [canRequestNewToken, setCanRequestNewToken] = useState<boolean>(true);
  const [hasRequestedToken, setHasRequestedToken] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [showEmailInput, setShowEmailInput] = useState<boolean>(false);
  const [tokenRequestSuccess, setTokenRequestSuccess] = useState(false);
  const [originalError, setOriginalError] = useState("");

  const handleSubmit = (mutateFunc) => {
    if (password != passwordRepeat) {
      return setError("The passwords do not match");
    }

    const token = queryParams.get("reset_password_token");
    mutateFunc({ variables: { password, token } }).then(({ data }) => {
      const res = data.reset_password;
      if (res.errors) {
        const errorMsg = res.errors.join("\n");
        setError(errorMsg);
        setOriginalError(errorMsg);
      }
      if (res.success) {
        setSuccess(true);
      }
    });
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const invalidToken = (originalError || error).toLowerCase().includes("invalid token");
  const isExpiredToken = (originalError || error).toLowerCase().includes("link has expired");
  const isUsedToken = (originalError || error).toLowerCase().includes("already confirmed");

  // Handle auto-redirect for invalid or used tokens
  useEffect(() => {
    if (invalidToken || isUsedToken) {
      setRedirectCountdown(10);
    }
  }, [invalidToken, isUsedToken]);

  // Countdown timer for redirect
  useEffect(() => {
    if (redirectCountdown !== undefined && redirectCountdown > 0) {
      const timer = setTimeout(() => {
        setRedirectCountdown(redirectCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (redirectCountdown === 0) {
      window.location.href = "/";
    }
    return undefined;
  }, [redirectCountdown]);

  // Start countdown after first token request
  useEffect(() => {
    if (requestTokenCountdown > 0) {
      const timer = setTimeout(() => {
        setRequestTokenCountdown((prev) => {
          if (prev <= 1) {
            setCanRequestNewToken(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [requestTokenCountdown]);

  const handleRequestNewToken = (mutateFunc) => {
    if (!isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setCanRequestNewToken(false);
    setRequestTokenCountdown(30);
    setHasRequestedToken(true);

    mutateFunc({ variables: { email } })
      .then(({ data }) => {
        setTokenRequestSuccess(true);
        setShowEmailInput(false);
        setEmail("");
        setError("");
      })
      .catch((res) => {
        const errorMessage = res.graphQLErrors?.[0]?.message
          ? JSON.parse(res.graphQLErrors[0].message)[0]
          : "Failed to send reset email";
        setError(errorMessage);
        setCanRequestNewToken(true);
        setRequestTokenCountdown(0);
      });
  };

  const renderForm = (mutateFunc) => {
    const canSubmit = () => {
      return validatePassword(password) && password === passwordRepeat;
    };

    return (
      <form id="reset-password-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(mutateFunc); }}>
        <TextField
          type="password"
          label="Password"
          value={password}
          onChange={(e, v) => setPassword(v ?? "")}
          data-testid="password"
        />
        <TextField
          type="password"
          label="Repeat Password"
          value={passwordRepeat}
          onChange={(e, v) => setPasswordRepeat(v ?? "")}
          data-testid="repeat-password"
        />
        <div className="text-center">
          <PrimaryButton type="submit" style={{ padding: 0 }} disabled={!canSubmit()} data-testid="login-btn">
            Reset Password
          </PrimaryButton>
        </div>
      </form>
    );
  };

  const renderFailure = () => {
    return (
      <div>
        {invalidToken && (
          <div>
            <h1>Invalid Reset Token</h1>
            <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
              The password reset token is invalid.
            </MessageBar>
            <p style={{ marginTop: "16px", textAlign: "center" }}>
              Redirecting to login page in {redirectCountdown} seconds...
            </p>
            <div className="text-center" style={{ marginTop: "16px" }}>
              <PrimaryButton
                onClick={() => window.location.href = "/"}
                style={{ padding: 0 }}
              >
                Go to Login Now
              </PrimaryButton>
            </div>
          </div>
        )}

        {isExpiredToken && (
          <div>
            <h1>Password Reset Link Expired</h1>
            {!tokenRequestSuccess && (
              <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
                {error}
              </MessageBar>
            )}

            {/* Email input section */}
            {showEmailInput && (
              <div style={{ marginTop: "20px" }}>
                <TextField
                  label="Enter your email address"
                  value={email}
                  onChange={(e, v) => setEmail(v ?? "")}
                  placeholder="user@example.com"
                  data-testid="email-input"
                />
              </div>
            )}

            {/* Success message after token request */}
            {tokenRequestSuccess && (
              <MessageBar
                messageBarType={MessageBarType.success}
              >
                A new password reset link has been sent to your email address. Please check your inbox.
              </MessageBar>
            )}

            {/* Request button and countdown */}
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              {gqlMutation(ForgotPasswordMutation, (mutateFunc) => (
                <>
                  {!showEmailInput && canRequestNewToken
                    ? (
                    <PrimaryButton
                      onClick={() => setShowEmailInput(true)}
                      data-testid="request-new-token-btn"
                    >
                      Request New Reset Link
                    </PrimaryButton>
                      )
                    : showEmailInput && canRequestNewToken
                      ? (
                    <div>
                      <PrimaryButton
                        onClick={() => handleRequestNewToken(mutateFunc)}
                        disabled={!email}
                        data-testid="send-reset-link-btn"
                        style={{ marginRight: "10px" }}
                      >
                        Send Reset Link
                      </PrimaryButton>
                      <DefaultButton
                        onClick={() => {
                          setShowEmailInput(false);
                          setEmail("");
                        }}
                        style={{ padding: 0 }}
                      >
                        Cancel
                      </DefaultButton>
                    </div>
                        )
                      : (
                    <div>
                      <p>You can request a new reset link in {requestTokenCountdown} seconds</p>
                      <DefaultButton disabled>
                        Request New Token ({requestTokenCountdown}s)
                      </DefaultButton>
                    </div>
                        )}
                </>
              ))}
            </div>

            <div className="text-center" style={{ marginTop: "16px" }}>
              <Link to="/">
                Back to Login
              </Link>
            </div>
          </div>
        )}

        {isUsedToken && (
          <div>
            <h1>Token Already Used</h1>
            <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
              This password reset token has already been used.
            </MessageBar>
            <p style={{ marginTop: "16px", textAlign: "center" }}>
              Redirecting to login page in {redirectCountdown} seconds...
            </p>
            <div className="text-center" style={{ marginTop: "16px" }}>
              <PrimaryButton
                onClick={() => window.location.href = "/"}
                style={{ padding: 0 }}
              >
                Go to Login Now
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSuccess = () => (
    <>
      <MessageBar messageBarType={MessageBarType.success}>Your password has been successfully updated.</MessageBar>
      <div className="text-center">
        <PrimaryButton
          onClick={() => (window.location.href = "/")}
          data-testid="login-btn"
          style={{ padding: 0 }}
          type="submit"
        >
          Login
        </PrimaryButton>
      </div>
    </>
  );

  if (invalidToken || isExpiredToken || isUsedToken) {
    return (
      <LoginLayout>
        <div className="login-panel">
          <div className="corsis-logo" />
          <div className="panel-body">
            {renderFailure()}
          </div>
        </div>
      </LoginLayout>
    );
  }

  return (
    <LoginLayout>
      <div className="login-panel">
        <div className="corsis-logo" />
        <div className="panel-body">
          <h1>Change Your Password</h1>
          <PasswordRequirements password={password} />
          {error && (
            <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
              <pre style={{ margin: 0, background: "none", border: "none", padding: 0, fontFamily: "inherit", whiteSpace: "pre-wrap" }}>{error}</pre>
            </MessageBar>
          )}
          {gqlMutation(ResetPasswordMutation, (mutateFunc) => (success ? renderSuccess() : renderForm(mutateFunc)))}
        </div>
      </div>
    </LoginLayout>
  );
}

export default ResetPassword;
